'use memo'

import {
  RenderedSpecialCondition,
  SpecialConditionData,
  useConstructConditionClauseText,
} from '@urbanx/agx.campaigns.client'
import { AgxEmptyState, AgxSpacer } from '@urbanx/agx.ui-components2'
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AddSpecialConditionButton } from '../contract-information/modals/specialConditions/AddSpecialConditionButton'
import SpecialConditionCollapsiblePanel from '../contract-information/panels/SpecialConditionCollapsiblePanel'

interface ContractComplianceSpecialConditionsProps
  extends TaskModalPageProps {
  specialConditions?: SpecialConditionData[]
  onSpecialConditionsUpdated?: (
    specialConditions: SpecialConditionData[]
  ) => void
}

const EMPTY_SPECIAL_CONDITIONS: SpecialConditionData[] = []

const ContractComplianceSpecialConditions = ({
  campaign,
  onSpecialConditionsUpdated,
  specialConditions = EMPTY_SPECIAL_CONDITIONS,
  task,
}: ContractComplianceSpecialConditionsProps) => {
  const [renderedSpecialConditions, setRenderedSpecialConditions] = useState<
    RenderedSpecialCondition[] | undefined
  >(undefined)

  const contractId = task.contractId ?? ''
  const campaignId = campaign.campaignId ?? ''

  const { constructConditionClauseText } = useConstructConditionClauseText({
    onSuccess: (conditionTextBlocks) => {
      setRenderedSpecialConditions(conditionTextBlocks)
    },
    onError: () => {
      setRenderedSpecialConditions(undefined)
    },
  })

  useEffect(() => {
    if (!campaignId || !contractId) return

    if (specialConditions.length === 0) {
      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.length === 0 ? prevRendered : []
      )
      return
    }

    constructConditionClauseText({
      campaignId,
      contractId,
      model: specialConditions,
    })
  }, [campaignId, constructConditionClauseText, contractId, specialConditions])

  const onAddCustomCondition = useCallback(
    (customSpecialCondition: SpecialConditionData) => {
      onSpecialConditionsUpdated?.([
        ...specialConditions,
        customSpecialCondition,
      ])
    },
    [onSpecialConditionsUpdated, specialConditions]
  )

  const onDueDateUpdated = useCallback(
    (
      conditionId: string,
      clauseId: string,
      newDueDate: string | undefined
    ) => {
      onSpecialConditionsUpdated?.(
        specialConditions.map((condition) => {
          if (condition.id !== conditionId) return condition

          return {
            ...condition,
            clauses: condition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause

              return {
                ...clause,
                overriddenDueDate: {
                  referenceDate: newDueDate,
                },
              }
            }),
          }
        })
      )

      setRenderedSpecialConditions((prevRendered) =>
        prevRendered?.map((renderedCondition) => {
          if (renderedCondition.id !== conditionId) return renderedCondition

          return {
            ...renderedCondition,
            clauses: renderedCondition.clauses?.map((clause) => {
              if (clause.clauseId !== clauseId) return clause

              return {
                ...clause,
                dueDate: newDueDate,
              }
            }),
          }
        })
      )
    },
    [onSpecialConditionsUpdated, specialConditions]
  )

  const onConditionDeleted = useCallback(
    (conditionId: string) => {
      onSpecialConditionsUpdated?.(
        specialConditions.filter((condition) => condition.id !== conditionId)
      )
    },
    [onSpecialConditionsUpdated, specialConditions]
  )

  const canShowEmptyState = useMemo(
    () =>
      specialConditions.length === 0 &&
      renderedSpecialConditions?.length === 0,
    [renderedSpecialConditions?.length, specialConditions.length]
  )

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-xl">
      {canShowEmptyState && (
        <AgxEmptyState
          variant="minimal"
          label="No special conditions added."
        />
      )}
      {renderedSpecialConditions?.map((renderedCondition) => (
        <SpecialConditionCollapsiblePanel
          key={renderedCondition.id ?? renderedCondition.conditionType}
          renderedSpecialCondition={renderedCondition}
          onDeleteCondition={onConditionDeleted}
          skipDeleteConfirmation
          campaignId={campaignId}
          contractId={contractId}
          onDueDateUpdated={onDueDateUpdated}
        />
      ))}
      <AddSpecialConditionButton
        id="contract-compliance-add-special-condition-button"
        label="Add Special Conditions"
        existingConditions={specialConditions}
        onAddSpecialCondition={onAddCustomCondition}
        campaignId={campaignId}
      />
    </AgxSpacer>
  )
}

export default ContractComplianceSpecialConditions
